import React, { useRef, useEffect } from 'react';

export const DraggableLabel = ({ 
    label, 
    value, 
    onChange, 
    onClick,
    className = '', 
    title = 'Drag to adjust value',
    sensitivity = 0.2,
    children
}) => {
    const isDragging = useRef(false);
    const startValRef = useRef(0);
    const unitRef = useRef('');
    const isDecimalRef = useRef(false);
    const wasNumberTypeRef = useRef(false);
    const accumulatedDeltaRef = useRef(0);

    const parseValue = (val) => {
        if (val === undefined || val === null || val === '') return null;
        const match = String(val).match(/^(-?[\d.]+)(.*)$/);
        if (!match) return null;
        return {
            num: parseFloat(match[1]),
            unit: match[2] || '',
            isDecimal: match[1].includes('.') || (typeof val === 'number' && !Number.isInteger(val)),
            wasNumberType: typeof val === 'number'
        };
    };

    const handleMouseDown = (e) => {
        const parsed = parseValue(value);
        if (!parsed) {
            if (onClick) onClick(e);
            return;
        }

        e.preventDefault();
        // e.stopPropagation(); 
        
        startValRef.current = parsed.num;
        unitRef.current = parsed.unit;
        isDecimalRef.current = parsed.isDecimal;
        wasNumberTypeRef.current = parsed.wasNumberType;
        accumulatedDeltaRef.current = 0;
        isDragging.current = true;

        const target = e.target;
        const requestPointerLock = target.requestPointerLock || target.mozRequestPointerLock || target.webkitRequestPointerLock;
        if (requestPointerLock) {
            requestPointerLock.call(target);
        }

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        document.body.style.cursor = 'ew-resize';
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;

        const { movementX } = e;
        if (movementX === undefined) return; 

        accumulatedDeltaRef.current += movementX;
        
        const step = isDecimalRef.current ? 0.1 : 1;
        
        const effectiveSensitivity = e.shiftKey ? (sensitivity * 0.1) : sensitivity;
        const virtualUnits = accumulatedDeltaRef.current * effectiveSensitivity;
        const change = Math.round(virtualUnits / step) * step;

        const newVal = startValRef.current + change;
        const rounded = isDecimalRef.current 
            ? Math.round(newVal * 100) / 100 
            : Math.round(newVal);

        const resultString = `${rounded}${unitRef.current}`;
        const result = wasNumberTypeRef.current ? parseFloat(resultString) : resultString;
        
        onChange(result);
    };

    const handleMouseUp = (e) => {
        isDragging.current = false;
        
        const exitPointerLock = document.exitPointerLock || document.mozExitPointerLock || document.webkitExitPointerLock;
        if (exitPointerLock) {
            exitPointerLock.call(document);
        }

        document.body.style.cursor = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Simulate click if barely moved (allows binding popup to work on numeric fields)
        if (Math.abs(accumulatedDeltaRef.current) < 2 && onClick) {
             onClick(e);
        }
    };

    const isNumeric = parseValue(value) !== null;
    
    const combinedClassName = `${className} ${isNumeric ? 'cursor-ew-resize' : ''}`.trim();
    const finalTitle = isNumeric ? title : (children ? '' : label);

    return (
        <label 
            className={combinedClassName}
            title={finalTitle}
            onMouseDown={handleMouseDown}
        >
            {children || label}
        </label>
    );
};